import { apiClient } from "@/services/api";
import type { MeetingDto, ListMeetingsResponse } from "@/types";
import { logError } from "@/lib/error-handling";

/** API resource for a meeting provider, e.g. "gong_calls" or "fathom_meetings". */
export type MeetingResource = "gong_calls" | "fathom_meetings";

export async function getMeetings(
  resource: MeetingResource,
  params: {
    page?: number;
    pageSize?: number;
    sortField?: string | null;
    sortDir?: "ASC" | "DESC";
    status?: string | null;
  } = {}
): Promise<ListMeetingsResponse> {
  const {
    page = 0,
    pageSize = 50,
    sortField,
    sortDir = "DESC",
    status,
  } = params;
  try {
    return await apiClient<ListMeetingsResponse>(`/api/${resource}`, {
      method: "GET",
      params: {
        limit: String(pageSize),
        offset: String(page * pageSize),
        sortDir,
        ...(status != null ? { status } : {}),
        ...(sortField != null ? { sortField } : {}),
      },
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "getMeetings");
    throw error;
  }
}

export async function getMeetingById(
  resource: MeetingResource,
  id: string
): Promise<MeetingDto> {
  try {
    return await apiClient<MeetingDto>(`/api/${resource}/${id}`);
  } catch (error) {
    logError(error, "getMeetingById");
    throw error;
  }
}

export async function updateMeetingAccount(
  resource: MeetingResource,
  id: string,
  existingAccountId: string | null
): Promise<MeetingDto> {
  try {
    return await apiClient<MeetingDto>(`/api/${resource}/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ account: { existingAccountId } }),
    });
  } catch (error) {
    logError(error, "updateMeetingAccount");
    throw error;
  }
}

export async function applyMeeting(
  resource: MeetingResource,
  id: string
): Promise<MeetingDto> {
  try {
    return await apiClient<MeetingDto>(`/api/${resource}/${id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
  } catch (error) {
    logError(error, "applyMeeting");
    throw error;
  }
}

export async function updateMeetingStatus(
  resource: MeetingResource,
  id: string,
  status: "review" | "archived"
): Promise<MeetingDto> {
  try {
    return await apiClient<MeetingDto>(`/api/${resource}/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
  } catch (error) {
    logError(error, "updateMeetingStatus");
    throw error;
  }
}
